// src/shared/services/deadlineCalculationService.ts
// 
// Shared Service für die Berechnung von Deadline-Daten pro Tag.
// Wird vom Urlaubsplaner genutzt, um eine Heatmap mit Deadline-Dichte anzuzeigen.
// 
// WICHTIG: Die Heatmap zeigt die GRUNDSÄTZLICHE Fristenlast pro Tag,
// unabhängig vom aktuellen Arbeitsmonat oder Erledigt-Status der Kunden.
// So können Mitarbeiter sehen, wie viel Arbeit an einem Tag generell anfällt,
// auch wenn sie Urlaub Monate im Voraus planen.
//
// OFFSET-LOGIK: Deadlines werden nicht nur für den angezeigten Monat als
// Abrechnungsmonat berechnet, sondern auch für den Vormonat. Das ist notwendig,
// weil Kunden mit monat_offset (z.B. +1) oder lohnsteuer_zeitraum "monatlich_versetzt"
// Fristen haben, die im FOLGEMONAT des Abrechnungsmonats liegen.
// Beispiel: Abrechnungsmonat Februar mit offset +1 → Frist fällt im März.

import { supabase } from './supabase';
import { Customer } from '../types';
import { calculateEffectiveLohnlaufDate, calculateLohnscheinVersandDate, calculateLohnsteuerDeadline } from '../utils/dateUtils';
import { hasLohnscheinVersand } from '../utils/deadlineUtils';
import { customerIndividualDeadlineService } from '../../services/customer/CustomerIndividualDeadlineService';

/**
 * Ergebnis der Deadline-Berechnung für einen Monat:
 * Map von Datum-String (YYYY-MM-DD) → Anzahl Deadlines an diesem Tag
 */
export interface DeadlineHeatmapData {
  [datum: string]: number;
}

class DeadlineCalculationService {

  /**
   * Berechnet die Deadline-Heatmap für einen bestimmten Monat.
   * 
   * Zeigt die GRUNDSÄTZLICHE Fristenlast: Alle Kunden-Deadlines werden so berechnet,
   * als ob der angezeigte Monat der Abrechnungsmonat wäre. Erledigt-Status wird ignoriert.
   * 
   * Zusätzlich wird der Vormonat als Abrechnungsmonat berücksichtigt, damit Deadlines
   * mit monat_offset oder monatlich_versetzt korrekt im Zielmonat erscheinen.
   * 
   * @param monat - Monat (1-12)
   * @param jahr - Jahr
   * @param staffId - Staff-ID des Mitarbeiters (null = alle Kunden / Admin-Ansicht)
   * @returns Map von Datum-String → Anzahl Deadlines
   */
  async getDeadlineHeatmapForMonth(
    monat: number,
    jahr: number,
    staffId: string | null
  ): Promise<DeadlineHeatmapData> {
    try {
      // 1. Kunden laden (gefiltert nach zuständigem Mitarbeiter oder alle)
      let query = supabase
        .from('customers')
        .select(`
          *,
          zustaendiger_mitarbeiter:xtax_staff!customers_zustaendiger_mitarbeiter_fkey (
            id,
            full_name,
            initials
          )
        `)
        .eq('deleted', false);

      if (staffId) {
        query = query.eq('zustaendiger_mitarbeiter_id', staffId);
      }

      const { data: customersData, error: customersError } = await query;

      if (customersError || !customersData || customersData.length === 0) {
        return {};
      }

      // 2. Monthly Deadlines laden (für Lohnsteuer + SV-Beiträge)
      // Lade Vormonat, aktuellen Monat UND Folgemonat:
      // - Vormonat: als Abrechnungsmonat für Deadlines mit offset/versetzt
      // - Aktueller Monat: als Abrechnungsmonat (Standard)
      // - Folgemonat: für "monatlich_versetzt" mit aktuellem Monat als Abrechnungsmonat
      const prevMonth = monat === 1 ? 12 : monat - 1;
      const prevMonthYear = monat === 1 ? jahr - 1 : jahr;
      const nextMonth = monat === 12 ? 1 : monat + 1;
      const nextMonthYear = monat === 12 ? jahr + 1 : jahr;

      const { data: deadlinesData } = await supabase
        .from('monthly_deadlines')
        .select('*')
        .or(
          `and(year.eq.${prevMonthYear},month.eq.${prevMonth}),and(year.eq.${jahr},month.eq.${monat}),and(year.eq.${nextMonthYear},month.eq.${nextMonth})`
        );

      // 3. Individuelle wiederkehrende Deadlines laden
      const customerIds = customersData.map((c: Customer) => c.id);
      const individualDeadlinesMap = await customerIndividualDeadlineService
        .getIndividualDeadlinesForCustomers(customerIds);

      // 4. Pro Kunde die Deadlines berechnen
      // Wir berechnen für ZWEI Abrechnungsmonate: den angezeigten UND den Vormonat.
      // So erfassen wir Deadlines, die durch Offsets im angezeigten Monat landen.
      const heatmap: DeadlineHeatmapData = {};

      const abrechnungsMonate = [
        { abrJahr: prevMonthYear, abrMonat: prevMonth },
        { abrJahr: jahr, abrMonat: monat }
      ];

      for (const customer of customersData as Customer[]) {
        const deadlineDates: Date[] = [];

        for (const { abrJahr, abrMonat } of abrechnungsMonate) {

          // Lohnlauf 1 + 2 via SSoT (calculateEffectiveLohnlaufDate).
          // status=null ist hier bewusst: Die Heatmap zeigt die GRUNDSÄTZLICHE
          // Fristenlast für die Vorausplanung — Anlieferungsmarkierungen existieren
          // für zukünftige Monate naturgemäß nicht, daher greift immer der fixe
          // Termin (identisches Verhalten wie vor dem Frist-ab-Anlieferung-Feature).

          // Lohnlauf 1
          if (customer.erster_lohnlauf_tag) {
            const date = calculateEffectiveLohnlaufDate(customer, 1, abrJahr, abrMonat, null);
            if (date) {
              deadlineDates.push(date);
            }
          }

          // Lohnlauf 2
          if (customer.zweiter_lohnlauf_tag) {
            const date = calculateEffectiveLohnlaufDate(customer, 2, abrJahr, abrMonat, null);
            if (date) {
              deadlineDates.push(date);
            }
          }

          // Lohnschein-Versand an Arbeitnehmer (Stufe 3.2) — Grundlast-Muster:
          // monatlich wiederkehrende Frist, Verschiebung/Offset via SSoT. Fester
          // Tag oder Bezug; bezugStatus bleibt null (Grundlast kennt keine
          // Anlieferung), eine relative Frist faellt damit auf den festen
          // Lohnlauf-Termin der Versand-Periode zurueck.
          if (hasLohnscheinVersand(customer)) {
            const date = calculateLohnscheinVersandDate(customer, abrJahr, abrMonat);
            if (date) {
              deadlineDates.push(date);
            }
          }

          // Lohnsteuer
          const lohnsteuerDate = calculateLohnsteuerDeadline(
            abrJahr,
            abrMonat,
            customer.lohnsteuer_zeitraum,
            (deadlinesData || []) as Array<{ year: number; month: number; lohnsteuer_frist: string | null }>
          );
          if (lohnsteuerDate) {
            deadlineDates.push(lohnsteuerDate);
          }

          // SV-Beiträge (haben keinen Offset, nur für den direkten Abrechnungsmonat)
          if (customer.sv_pflicht) {
            const deadline = ((deadlinesData || []) as Array<{ year: number; month: number; beitragsnachweis_frist: string | null }>)
              .find(d => d.year === abrJahr && d.month === abrMonat);
            if (deadline?.beitragsnachweis_frist) {
              deadlineDates.push(new Date(deadline.beitragsnachweis_frist));
            }
          }

          // Individuelle wiederkehrende Deadlines
          const individualDeadlines = individualDeadlinesMap[customer.id] || [];
          for (const indDeadline of individualDeadlines) {
            if (indDeadline.is_recurring) {
              // customer wird für relative Fristen benötigt (Lohnlauf-Anker);
              // bezugStatus bleibt null (Grundlast), eine relative Frist fällt
              // damit auf den festen Lohnlauf-Termin des Monats zurück.
              const date = customerIndividualDeadlineService.calculateIndividualDeadlineDate(
                abrJahr,
                abrMonat,
                indDeadline,
                customer
              );
              if (date) {
                deadlineDates.push(date);
              }
            }
          }
        }

        // Deadline-Daten auf den ANGEZEIGTEN Monat filtern und in Heatmap eintragen
        // Dadurch landen nur Deadlines, die tatsächlich im sichtbaren Monat fällig sind
        for (const date of deadlineDates) {
          if (date.getFullYear() === jahr && date.getMonth() + 1 === monat) {
            const dateKey = `${jahr}-${String(monat).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            heatmap[dateKey] = (heatmap[dateKey] || 0) + 1;
          }
        }
      }

      return heatmap;

    } catch (error) {
      console.error('Error calculating deadline heatmap:', error);
      return {};
    }
  }
}

export const deadlineCalculationService = new DeadlineCalculationService();
